export const translations = {
  en: {
    // Common
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      submit: 'Submit',
      close: 'Close',
      confirm: 'Confirm',
      search: 'Search',
      filter: 'Filter',
      actions: 'Actions',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      loading: 'Loading...',
      noData: 'No data',
      success: 'Success',
      error: 'Error',
      viewDetails: 'View Details',
      logout: 'Logout',
      login: 'Login',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      role: 'Role',
      groups: 'Groups',
      description: 'Description',
      version: 'Version',
      createdAt: 'Created',
      updatedAt: 'Updated',
      all: 'All',
      view: 'View',
      more: 'more',
    },

    // Roles
    roles: {
      admin: 'Admin',
      creator: 'Creator',
      user: 'User',
    },

    // Status
    status: {
      approved: 'Approved',
      rejected: 'Rejected',
      pending: 'Pending',
    },

    // Request types
    requestTypes: {
      create: 'New App',
      update: 'Update App',
      delete: 'Delete App',
    },

    // Landing page
    landing: {
      title: 'Estha for macOS',
      titleSuffix: '(My Org)',
      subtitle: 'Enterprise AI App Distribution Platform',
      heroTitle1: 'Your Enterprise',
      heroTitle2: 'AI App Center',
      heroDescription: 'Securely distribute enterprise AI apps with privacy-first design. Manage user permissions flexibly, let employees access exclusive AI tools with confidence.',
      privacyBadge: 'Privacy Protected',
      adminLogin: 'Admin Login',
      creatorLogin: 'Creator Login',
      features: 'Core Features',
      feature1Title: 'User & Permission Management',
      feature1Desc: 'Bulk import users, flexible group tags, precise AI app access control',
      feature2Title: 'App Review & Publishing',
      feature2Desc: 'Creators submit AI apps, admins review, auto-publish to authorized users',
      feature3Title: 'Privacy & Security',
      feature3Desc: 'Enterprise-grade privacy protection. Only authenticated users can access exclusive AI apps tailored for your organization',
      howItWorks: 'How It Works',
      step1Title: 'Admin Configuration',
      step1Desc: 'Admin logs in, imports employee list, sets up groups and role permissions',
      step2Title: 'Creator Submission',
      step2Desc: 'Creators build AI apps on estha.ai, submit to enterprise center with visibility scope',
      step3Title: 'Secure Review & Publish',
      step3Desc: 'Admin reviews apps, approved ones auto-publish to authorized users with secure access',
      step4Title: 'Private Access',
      step4Desc: 'Employees login to Estha for macOS, securely access and use their authorized AI apps',
      demoAccounts: 'Demo Accounts',
      demoAccountsDesc: 'Use these accounts to explore (any password)',
      adminAccount: 'Admin Account',
      creatorAccount: 'Creator Account',
      helpDocs: 'Documentation',
      contactUs: 'Contact Us',
      copyright: '© 2025 Estha AI. All rights reserved.',
      loginTitle: 'Login',
      loginAdminDesc: 'Login as admin to manage users and review apps',
      loginCreatorDesc: 'Login as creator to submit and manage apps',
      enterEmail: 'Enter your email',
      enterPassword: 'Enter your password',
      userNotFound: 'User not found, please check your email',
      noAdminPermission: 'This account does not have admin permissions',
      noCreatorPermission: 'This account does not have creator permissions',
      loginFailed: 'Login failed, please try again',
    },

    // Admin
    admin: {
      dashboard: 'Dashboard',
      title: 'Admin Panel',
      overview: 'Overview',
      totalUsers: 'Total Users',
      activeUsers: 'active',
      totalCreators: 'Creators',
      canSubmitApps: 'can submit apps',
      pendingReviews: 'Pending Reviews',
      needsAction: 'needs action',
      publishedApps: 'Published Apps',
      visibleToUsers: 'visible to users',
      pendingRequests: 'Pending Requests',
      noPendingRequests: 'No pending requests',
      quickActions: 'Quick Actions',

      // User Management
      userManagement: 'User Management',
      userManagementDesc: 'Manage enterprise users, assign roles and groups',
      bulkImport: 'Bulk Import',
      addUser: 'Add User',
      manageGroups: 'Manage Groups',
      searchUsers: 'Search name or email...',
      allRoles: 'All Roles',
      allGroups: 'All Groups',
      deleteSelected: 'Delete Selected',
      userInfo: 'User Info',
      noUsers: 'No users found',
      editUser: 'Edit User',
      addUserTitle: 'Add User',
      editUserDesc: 'Modify user information',
      addUserDesc: 'Add a new user to the system',
      emailExists: 'This email already exists',
      invalidEmail: 'Please enter a valid email',
      selectGroups: 'Select Groups',
      roleUser: 'User - can only view and use apps',
      roleCreator: 'Creator - can submit apps',
      roleAdmin: 'Admin - can manage everything',
      usersDeleted: 'Selected users deleted',
      userUpdated: 'User updated successfully',
      userCreated: 'User created successfully',
      userDeleted: 'User deleted successfully',
      passwordRequired: 'Password is required for new user',
      export: 'Export',
      showingUsers: 'Showing {from}-{to} of {total}',
      newPassword: 'New Password',
      leaveBlankToKeep: 'Leave blank to keep current password',
      noGroups: 'No groups available',
      groupCreated: 'Group created successfully',
      groupDeleted: 'Group deleted successfully',

      // Bulk Import
      bulkImportTitle: 'Bulk Import Users',
      bulkImportDesc: 'Import users via CSV file',
      csvFormat: 'CSV Format Guide',
      csvFormatDesc: 'Please ensure CSV file contains these columns (header row required):',
      csvExample: 'email,name,role,groups\nuser1@company.com,John Doe,user,All Company;HR\ndev@company.com,Jane Dev,creator,All Company;Engineering',
      csvNote: '* Role options: admin, creator, user\n* Multiple groups separated by semicolon (;)',
      uploadCSV: 'Click or drag to upload CSV file',
      csvSupport: 'Supports .csv format',
      importSuccess: 'Successfully imported {count} users',
      importFailed: '{count} failed',

      // Group Management
      manageGroupsTitle: 'Manage Groups',
      manageGroupsDesc: 'Add or delete user group tags',
      newGroupName: 'Enter new group name',
      members: 'members',
      deleteGroupConfirm: 'Are you sure you want to delete group "{name}"?',

      // App Review
      appReview: 'App Review',
      appReviewDesc: 'Review app submissions from creators',
      pendingCount: '{count} pending',
      allRequests: 'All',
      reviewApp: 'Review',
      noReviews: 'No pending reviews',
      noReviewsDesc: 'No requests to review',
      submittedBy: 'Submitted by',
      submittedAt: 'Submitted',
      viewApp: 'View App',
      reviewTitle: 'Review App',
      reviewDesc: 'Review the "{name}" {type} request',
      appName: 'App Name',
      appLink: 'App Link',
      visibleScope: 'Visible Scope',
      reviewNote: 'Review Note (required for rejection)',
      reviewNotePlaceholder: 'Enter review comments...',
      approve: 'Approve',
      reject: 'Reject',
      approved: 'Approved',
      rejected: 'Rejected',
      requestApproved: 'Request approved successfully',
      requestRejected: 'Request rejected successfully',
      reviewNoteRequired: 'Review note is required for rejection',
      showingReviews: 'Showing {from}-{to} of {total}',

      // Published Apps
      publishedAppsTitle: 'Published Apps',
      publishedAppsDesc: 'View approved and published apps ({count} total)',
      searchApps: 'Search app name or description...',
      noApps: 'No published apps',
      groupCount: '{count} groups',
      showingApps: 'Showing {from}-{to} of {total}',

      // Settings
      settings: 'Page Settings',
      settingsDesc: 'Customize your enterprise app center appearance and branding',
      saveChanges: 'Save Changes',
      saved: 'Saved',
      settingsSaved: 'Settings saved successfully',
      basicInfo: 'Basic Information',
      basicInfoDesc: 'Set organization name and welcome message',
      orgName: 'Organization Name',
      orgNamePlaceholder: 'e.g., Estha Enterprise',
      welcomeMessage: 'Welcome Message',
      welcomeMessagePlaceholder: 'e.g., Welcome to your Enterprise App Center',
      branding: 'Brand Images',
      brandingDesc: 'Upload logo and banner images',
      logo: 'Logo',
      logoUrl: 'Enter image URL or upload',
      logoSize: 'Recommended: 200x200px',
      banner: 'Banner',
      addBanner: 'Add banner image',
      bannerSize: 'Recommended: 1200x300px',
      themeColor: 'Theme Color',
      themeColorDesc: 'Choose your primary color',
      preview: 'Preview',
      previewDesc: 'macOS client enterprise page preview',
    },

    // Creator
    creator: {
      title: 'Creator Center',
      myApps: 'My Apps',
      myAppsDesc: 'Manage your submitted enterprise apps',
      submitApp: 'Submit App',
      submitNewApp: 'Submit New App',
      submitFirstApp: 'Submit Your First App',
      submissionHistory: 'Submission History',
      submissionHistoryDesc: 'View all app submissions and review status',
      noApps: "You haven't submitted any apps yet",
      pendingRequestsNotice: 'You have {count} pending request(s)',
      viewHistory: 'Go to',
      hasPendingRequest: 'Has pending request',

      // Submit App
      submitNewAppTitle: 'Submit New App',
      submitNewAppDesc: 'Fill in app info, wait for admin review after submission',
      backToMyApps: 'Back to My Apps',
      basicInfo: 'Basic Information',
      appName: 'App Name',
      appNamePlaceholder: 'Enter app name',
      appLink: 'App Link',
      appLinkHint: 'Enter the link to your app published on estha.ai',
      appDescription: 'App Description',
      appDescriptionPlaceholder: 'Describe the app features and purpose...',
      categoryTags: 'Category Tags',
      categoryTagsDesc: 'Add tags to help users discover your app',
      enterTag: 'Enter tag',
      suggestedTags: 'Suggested tags',
      visibleScope: 'Visible Scope',
      visibleScopeDesc: 'Select which users can see and use this app',
      selectGroups: 'Select Groups',
      addIndividuals: 'Add Individuals (Email)',
      submitForReview: 'Submit for Review',
      submitSuccess: 'App submitted successfully, waiting for admin review',

      // Edit App
      editApp: 'Edit App',
      editAppDesc: 'Changes require admin re-review',
      appNotFound: 'App not found',
      noPermission: 'You do not have permission to edit this app',
      submitUpdate: 'Submit Update',
      updateSubmitted: 'Update request submitted, waiting for admin review',

      // Delete App
      deleteApp: 'Delete App',
      deleteAppConfirm: 'Are you sure you want to delete "{name}"? This requires admin approval.',
      deleteAppNotice: 'After submitting the delete request, admin approval is needed. The app remains visible during review.',
      deleteReason: 'Reason for deletion (optional)',
      deleteReasonPlaceholder: 'Please explain why...',
      submitDeleteRequest: 'Submit Delete Request',
      deleteRequestSubmitted: 'Delete request submitted, waiting for admin review',

      // History
      noHistory: 'No submission history',
      submittedAt: 'Submitted on',
      reviewedAt: 'Reviewed on',
      reviewNoteLabel: 'Review Note: ',
    },

    // Suggested tags
    tags: {
      suggested: ['Tools', 'Productivity', 'Efficiency', 'Design', 'Development', 'Communication', 'Finance', 'HR', 'Legal', 'Marketing'],
    },

    // Validation messages
    validation: {
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidUrl: 'Please enter a valid URL',
      appNameRequired: 'Please enter app name',
      appLinkRequired: 'Please enter app link',
      appDescRequired: 'Please enter app description',
      permissionsRequired: 'Please select at least one group or add one email',
    },

    // Confirmations
    confirm: {
      deleteUser: 'Are you sure you want to delete this user?',
      deleteUsers: 'Are you sure you want to delete {count} selected users?',
      deleteApp: 'Are you sure you want to delete this app?',
    },
  },

  zh: {
    // Common
    common: {
      save: '保存',
      cancel: '取消',
      delete: '删除',
      edit: '编辑',
      add: '添加',
      submit: '提交',
      close: '关闭',
      confirm: '确认',
      search: '搜索',
      filter: '筛选',
      actions: '操作',
      status: '状态',
      active: '活跃',
      inactive: '停用',
      loading: '加载中...',
      noData: '暂无数据',
      success: '成功',
      error: '错误',
      viewDetails: '查看详情',
      logout: '退出登录',
      login: '登录',
      email: '邮箱',
      password: '密码',
      name: '姓名',
      role: '角色',
      groups: '群组',
      description: '描述',
      version: '版本',
      createdAt: '创建时间',
      updatedAt: '更新时间',
      all: '全部',
      view: '查看',
      more: '更多',
    },

    // Roles
    roles: {
      admin: '管理员',
      creator: '开发者',
      user: '用户',
    },

    // Status
    status: {
      approved: '已通过',
      rejected: '已拒绝',
      pending: '待审核',
    },

    // Request types
    requestTypes: {
      create: '新建应用',
      update: '更新应用',
      delete: '删除应用',
    },

    // Landing page
    landing: {
      title: 'Estha for macOS',
      titleSuffix: '(My Org)',
      subtitle: '企业专属 AI 应用分发平台',
      heroTitle1: '为您的企业打造',
      heroTitle2: '专属 AI 应用中心',
      heroDescription: '隐私优先，安全分发企业专属 AI 应用。灵活管理用户权限，让员工放心使用专属 AI 工具',
      privacyBadge: '隐私保护',
      adminLogin: '管理员登录',
      creatorLogin: '开发者登录',
      features: '核心功能',
      feature1Title: '用户与权限管理',
      feature1Desc: '批量导入用户，灵活设置群组标签，精确控制 AI 应用访问权限',
      feature2Title: '应用审核发布',
      feature2Desc: '开发者提交 AI 应用，管理员审核通过后自动发布到员工端',
      feature3Title: '隐私与安全',
      feature3Desc: '企业级隐私保护，仅授权用户可访问专为您组织定制的专属 AI 应用',
      howItWorks: '使用流程',
      step1Title: '管理员配置',
      step1Desc: '管理员登录后台，导入员工名单，设置群组和角色权限',
      step2Title: '开发者提交',
      step2Desc: '开发者在 estha.ai 创建 AI 应用后，提交到企业应用中心，指定可见范围',
      step3Title: '安全审核发布',
      step3Desc: '管理员审核应用，通过后安全发布到对应用户的应用列表',
      step4Title: '安全访问',
      step4Desc: '员工在 Estha for macOS 客户端登录后，安全访问和使用专属 AI 应用',
      demoAccounts: '演示账号',
      demoAccountsDesc: '使用以下账号体验系统功能（密码任意）',
      adminAccount: '管理员账号',
      creatorAccount: '开发者账号',
      helpDocs: '帮助文档',
      contactUs: '联系我们',
      copyright: '© 2025 Estha AI. All rights reserved.',
      loginTitle: '登录',
      loginAdminDesc: '使用管理员账号登录以管理用户、审核应用',
      loginCreatorDesc: '使用开发者账号登录以提交和管理应用',
      enterEmail: '请输入邮箱',
      enterPassword: '请输入密码',
      userNotFound: '用户不存在，请检查邮箱地址',
      noAdminPermission: '该账号没有管理员权限',
      noCreatorPermission: '该账号没有开发者权限',
      loginFailed: '登录失败，请重试',
    },

    // Admin
    admin: {
      dashboard: '管理概览',
      title: '管理后台',
      overview: '概览',
      totalUsers: '总用户数',
      activeUsers: '个活跃',
      totalCreators: '开发者数',
      canSubmitApps: '可提交应用',
      pendingReviews: '待审核',
      needsAction: '需要处理',
      publishedApps: '已发布应用',
      visibleToUsers: '对用户可见',
      pendingRequests: '待审核请求',
      noPendingRequests: '暂无待审核请求',
      quickActions: '快速操作',

      // User Management
      userManagement: '用户管理',
      userManagementDesc: '管理企业用户、分配角色和群组',
      bulkImport: '批量导入',
      addUser: '添加用户',
      manageGroups: '管理群组',
      searchUsers: '搜索用户名或邮箱...',
      allRoles: '全部角色',
      allGroups: '全部群组',
      deleteSelected: '删除选中',
      userInfo: '用户信息',
      noUsers: '暂无用户数据',
      editUser: '编辑用户',
      addUserTitle: '添加用户',
      editUserDesc: '修改用户信息',
      addUserDesc: '添加新用户到系统',
      emailExists: '该邮箱已存在',
      invalidEmail: '请输入有效的邮箱地址',
      selectGroups: '选择群组',
      roleUser: '用户 - 仅可查看和使用应用',
      roleCreator: '开发者 - 可提交应用',
      roleAdmin: '管理员 - 可管理所有内容',
      usersDeleted: '已删除选中的用户',
      userUpdated: '用户更新成功',
      userCreated: '用户创建成功',
      userDeleted: '用户删除成功',
      passwordRequired: '新用户必须设置密码',
      export: '导出',
      showingUsers: '显示 {from}-{to}，共 {total} 条',
      newPassword: '新密码',
      leaveBlankToKeep: '留空保持当前密码',
      noGroups: '暂无群组',
      groupCreated: '群组创建成功',
      groupDeleted: '群组删除成功',

      // Bulk Import
      bulkImportTitle: '批量导入用户',
      bulkImportDesc: '通过CSV文件批量导入用户',
      csvFormat: 'CSV格式说明',
      csvFormatDesc: '请确保CSV文件包含以下列（标题行必须）：',
      csvExample: '邮箱,姓名,角色,群组\nuser1@company.com,张三,user,全公司;HR部门\ndev@company.com,李四,creator,全公司;研发部门',
      csvNote: '* 角色可选值：admin, creator, user\n* 多个群组用分号(;)分隔',
      uploadCSV: '点击或拖拽上传CSV文件',
      csvSupport: '支持 .csv 格式',
      importSuccess: '成功导入 {count} 个用户',
      importFailed: '{count} 个失败',

      // Group Management
      manageGroupsTitle: '管理群组',
      manageGroupsDesc: '添加或删除用户群组标签',
      newGroupName: '输入新群组名称',
      members: '个成员',
      deleteGroupConfirm: '确定要删除群组 "{name}" 吗？',

      // App Review
      appReview: '应用审核',
      appReviewDesc: '审核开发者提交的应用请求',
      pendingCount: '（{count} 个待处理）',
      allRequests: '全部',
      reviewApp: '审核',
      noReviews: '暂无待审核的请求',
      noReviewsDesc: '暂无请求记录',
      submittedBy: '提交者',
      submittedAt: '提交于',
      viewApp: '查看应用',
      reviewTitle: '审核应用',
      reviewDesc: '请审核 "{name}" 的{type}请求',
      appName: '应用名称',
      appLink: '应用链接',
      visibleScope: '可见范围',
      reviewNote: '审核备注（拒绝时必填）',
      reviewNotePlaceholder: '输入审核意见...',
      approve: '通过',
      reject: '拒绝',
      approved: '已通过',
      rejected: '已拒绝',
      requestApproved: '请求已通过',
      requestRejected: '请求已拒绝',
      reviewNoteRequired: '拒绝时必须填写审核备注',
      showingReviews: '显示 {from}-{to}，共 {total} 条',

      // Published Apps
      publishedAppsTitle: '已发布应用',
      publishedAppsDesc: '查看已通过审核并发布的应用（共 {count} 个）',
      searchApps: '搜索应用名称或描述...',
      noApps: '暂无已发布的应用',
      groupCount: '{count} 群组',
      showingApps: '显示 {from}-{to}，共 {total} 个',

      // Settings
      settings: '页面设置',
      settingsDesc: '自定义企业应用中心的外观和品牌',
      saveChanges: '保存更改',
      saved: '已保存',
      settingsSaved: '设置保存成功',
      basicInfo: '基本信息',
      basicInfoDesc: '设置企业名称和欢迎语',
      orgName: '企业/组织名称',
      orgNamePlaceholder: '例如：Estha 企业版',
      welcomeMessage: '欢迎语',
      welcomeMessagePlaceholder: '例如：欢迎使用企业专属应用中心',
      branding: '品牌图片',
      brandingDesc: '上传Logo和Banner图片',
      logo: 'Logo',
      logoUrl: '输入图片URL或上传图片',
      logoSize: '建议尺寸：200x200px',
      banner: 'Banner',
      addBanner: '添加Banner图片',
      bannerSize: '建议尺寸：1200x300px',
      themeColor: '主题色',
      themeColorDesc: '选择主题颜色',
      preview: '预览效果',
      previewDesc: 'macOS 客户端企业页面预览',
    },

    // Creator
    creator: {
      title: '开发者中心',
      myApps: '我的应用',
      myAppsDesc: '管理您提交的企业应用',
      submitApp: '提交应用',
      submitNewApp: '提交新应用',
      submitFirstApp: '提交第一个应用',
      submissionHistory: '提交记录',
      submissionHistoryDesc: '查看所有应用提交和审核记录',
      noApps: '您还没有提交任何应用',
      pendingRequestsNotice: '您有 {count} 个待审核的请求',
      viewHistory: '请前往',
      hasPendingRequest: '有待处理的请求',

      // Submit App
      submitNewAppTitle: '提交新应用',
      submitNewAppDesc: '填写应用信息，提交后等待管理员审核',
      backToMyApps: '返回我的应用',
      basicInfo: '基本信息',
      appName: '应用名称',
      appNamePlaceholder: '输入应用名称',
      appLink: '应用链接',
      appLinkHint: '请输入您在 estha.ai 上发布的应用链接',
      appDescription: '应用描述',
      appDescriptionPlaceholder: '描述应用的功能和用途...',
      categoryTags: '分类标签',
      categoryTagsDesc: '添加标签帮助用户发现您的应用',
      enterTag: '输入标签',
      suggestedTags: '推荐标签',
      visibleScope: '可见范围',
      visibleScopeDesc: '选择哪些用户可以看到和使用此应用',
      selectGroups: '选择群组',
      addIndividuals: '添加个人（邮箱）',
      submitForReview: '提交审核',
      submitSuccess: '应用提交成功，等待管理员审核',

      // Edit App
      editApp: '编辑应用',
      editAppDesc: '修改应用信息后需要管理员重新审核',
      appNotFound: '应用不存在',
      noPermission: '您没有权限编辑此应用',
      submitUpdate: '提交更新',
      updateSubmitted: '更新请求已提交，等待管理员审核',

      // Delete App
      deleteApp: '删除应用',
      deleteAppConfirm: '确定要删除 "{name}" 吗？此操作需要管理员审核。',
      deleteAppNotice: '删除请求提交后需要管理员审核通过后才会生效。在审核期间，应用仍然对用户可见。',
      deleteReason: '删除原因（可选）',
      deleteReasonPlaceholder: '请说明删除原因...',
      submitDeleteRequest: '提交删除请求',
      deleteRequestSubmitted: '删除请求已提交，等待管理员审核',

      // History
      noHistory: '暂无提交记录',
      submittedAt: '提交于',
      reviewedAt: '审核于',
      reviewNoteLabel: '审核备注：',
    },

    // Suggested tags
    tags: {
      suggested: ['工具', '办公', '效率', '设计', '开发', '通讯', '财务', 'HR', '法务', '市场'],
    },

    // Validation messages
    validation: {
      required: '此字段为必填项',
      invalidEmail: '请输入有效的邮箱地址',
      invalidUrl: '请输入有效的URL',
      appNameRequired: '请输入应用名称',
      appLinkRequired: '请输入应用链接',
      appDescRequired: '请输入应用描述',
      permissionsRequired: '请至少选择一个群组或添加一个邮箱',
    },

    // Confirmations
    confirm: {
      deleteUser: '确定要删除该用户吗？',
      deleteUsers: '确定要删除选中的 {count} 个用户吗？',
      deleteApp: '确定要删除该应用吗？',
    },
  },
} as const;

export type Language = keyof typeof translations;
export type TranslationKeys = (typeof translations)['en'] | (typeof translations)['zh'];
